import { Injectable } from '@angular/core';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';

@Injectable({
    providedIn: 'root',
})
export class ProjectService {
    constructor(private datatask: DataTaskService) {}

    /**
     * Retrieves the project using the given params.
     *
     * @param params the params to get a project based of.
     * @datatask 904
     * @datataskName sp_ws_p2_project_get
     * @returns a promise containing the project, if any.
     */
    public async getProject(params: ProjectParams): Promise<ProjectGet> {
        return (await this.datatask.Post(904, params))[0];
    }

    /**
     * Saves the given project object, can be either a new or a existing one.
     *
     * @param project the project to save.
     * @datatask 1001
     * @datataskName sp_ws_p2_project_save
     * @returns promise containing possible errorcode and message.
     */
    public async saveProject(project: ProjectGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(1001, project))[0];
    }

    /**
     * @param params the project to delete.
     * @datatask 4031
     * @datataskName sp_ws_p2_project_delete
     * @returns promise containing validation.
     */
    public async deleteProject(project: ProjectGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(4031, project))[0];
    }

    /**
     * Retrieves the project menu page get for the given project keyno.
     *
     * @param params object with the keyno to get menu page get for.
     * @datatask 905
     * @datataskName sp_ws_p2_w_projectmenu_get
     * @returns a promise containing the project menu get.
     */
    public async getProjectMenu(params: ProjectMenuParams): Promise<ProjectMenu> {
        return (await this.datatask.Post(905, params))[0];
    }

    /**
     * Retrieves list of user that can be project managers.
     *
     * @param params configuration for the list.
     * @datatask 2747
     * @datataskName sp_ws_p2_portal_user_list_project_manager
     * @returns a promise containing the list user that can be project managers.
     */
    public getProjectManagers(params?: ProjectManagersParams): Promise<ProjectManagerItem[]> {
        return this.datatask.Post(2747, params);
    }

    /**
     * Retrieves list of minute precisions.
     *
     * @datatask 1426
     * @datataskName sp_ws_p2_timeregister_minutes_precision_list
     * @returns a promise containing a list of different minuteprecisions.
     */
    public getMinutePrecisions(): Promise<MinutePrecisionItem[]> {
        return this.datatask.Post(1426);
    }

    /**
     * TODO: move to docpack service when that is created.
     * @datatask
     * @datataskName sp_ws_p2_docpack_create
     * @returns a promise containing potential errorcode and message.
     */
    public async createDocPack(params: CreateDockPackParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3288, params))[0];
    }

    /**
     * Retrieves list of projects basedon the given params.
     *
     * @datatask 902
     * @datataskName sp_ws_p2_project_list
     * @param params filters for the list.
     * @returns promise containing list of projects based on the given params.
     */
    public async getProjects(params: GetProjectsParams): Promise<ProjectItem[]> {
        return this.datatask.Post(902, params);
    }

    /**
     * Retrieves page get for select project.
     *
     * @datatask 1002
     * @datataskName sp_ws_p2_w_selectproject_settings
     * @param params argtype and argvalue.
     * @returns promise containing page get for select project.
     */
    public async getSelectProject(params: GetSelectProjectParams): Promise<SelectProjectGet> {
        return (await this.datatask.Post(1002, params))[0];
    }
}
export interface GetSelectProjectParams {
    argtype: string;
    argvalue: string;
}

export interface SelectProjectGet {
    argtype: string;
    argvalue: string;
    searchtext: string;
    bg_projects_activestatus: 'ACTIVE' | 'NOTACTIVE';
    show_setting: '1' | '0';
}

export interface GetProjectsParams {
    searchtext?: string;
    businessco_no?: string | number;
    argtype: string;
    argvalue: string;
    bg_projects_activestatus: 'ALL' | 'ACTIVE' | 'NOTACTIVE';
}

export interface ProjectItem {
    item_id: string;
    item_name: string;
    item_path: string;
    item_glyphicon: string;
    item_glyphicon_color: string;
    orderby: string;
    item_state: string;
    item_parms: string;
    item_name_sub1: string;
    item_filtervalue: string;
}

export interface CreateDockPackParams {
    docpacktype_keyno: string | number;
    argtype: string;
    argvalue: string | number;
}

export interface ProjectMenuParams {
    project_keyno: string | number;
}

export interface ProjectMenu {
    project_keyno: string | number;
    project_name: string;
    active: '1' | '0';
    cust_no: string | number;
    cust_name: string;
    businessco_no: string | number;
    description: string;
    nbr_of_projectprosess: string | number;
    activities_badge: string;
    orders_badge: string;
    sum_ordervalue_locval: string | number;
    sum_purchasevalue_locval: string | number;
    sum_hours_total: string | number;
    purchases_badge: string;
    nbr_of_documents: string | number;
    nbr_of_pictures: string | number;
    show_button_pictures: '1' | '0';
    show_button_documents: '1' | '0';
    show_button_timesheetdiary: '1' | '0';
    show_button_gantt: '1' | '0';
    show_button_custactplanning: '1' | '0';
    show_paymentplans: '1' | '0';
    webpage_heading: string;
    gb_projectmenu_main: string;
    nbr_of_docpacktypes: string | number;
    docpacktype_keyno: string | number;
    show_tab_custactac: '1' | '0';
}

export interface MinutePrecisionItem {
    timeregister_minutes_precision: string;
    timeregister_minutes_precision_name: string;
    orderby: string;
}

export interface ProjectManagersParams {
    /**
     * Whether to add a option for 'all' project managers in the list.
     *
     * @default '0'
     */
    add_all?: '1' | '0';
}

export interface ProjectManagerItem {
    item_id: string;
    item_name: string;
    orderby: string;
}

export interface ProjectParams {
    project_keyno: string | number;
}

export interface ProjectGet {
    project_keyno: string;
    project_name: string;
    active: '1' | '0';
    cust_no: string | number;
    businessco_no: string;
    description: string;
    deladronpurch: '1' | '0';
    delivery_cust_no: string | number;
    delivery_cust_search: string;
    cust_search: string;
    customers_ref: string;
    cust_name: string;
    show_delivery_cust_name: string;
    delivery_search_active: string;
    cust_search_active: string;
    heading_cust_no: string;
    heading_delivery_cust_no: string;
    heading_planning: string;
    heading_contract: string;
    start: string;
    end: string;
    shift_id: string;
    timeregister_minutes_precision: string;
    heading_terms: string;
    pricelist_id: string;
    pricelist_id2: string;
    payment_id: string;
    contract_amount_ex_tax: string | number;
    contract_amount_incl_tax: string | number;
    max_outstanding_pst: string | number;
    max_outstanding_amount_ex_tax: string | number;
    max_outstanding_amount_incl_tax: string | number;
    tax_keyno: string;
    tax_pst: string | number;
    default_outstanding_pst: string | number;
    portal_user_keyno_project_manager: string;
    project_template: '1' | '0';
    show_delete_button: '1' | '0';
    show_settings: '1' | '0';
}
